/**
 * The six lead fields every sales-lead form on the site must capture, plus
 * the person's name. Rendered as a single reusable block so a field is never
 * accidentally missing from a form.
 *
 * Styling is supplied by the caller (labelStyle / inputStyle / className) so
 * each surface keeps its existing look — light, dark, or the blue CTA band.
 */
import type { CSSProperties, ReactNode } from "react";
import { AU_STATES, type LeadCoreValues } from "@/lib/lead-fields";
import FloatingField from "./FloatingField";

export interface LeadCoreFieldsProps {
  values: LeadCoreValues;
  errors: Partial<Record<keyof LeadCoreValues, string>>;
  onChange: (key: keyof LeadCoreValues, value: string) => void;
  /** Unique per form instance so label/input ids never collide. */
  idPrefix: string;
  disabled?: boolean;
  labelStyle?: CSSProperties;
  inputStyle?: CSSProperties;
  inputClassName?: string;
  errorClassName?: string;
  /**
   * `true` (default) renders a persistent label above each control.
   * `false` switches to floating labels — the label rests inside the control
   * and floats to the top border on focus/fill, so it is never lost once the
   * visitor starts typing.
   */
  showLabels?: boolean;
  /** Background painted behind a floated label chip (floating mode only). */
  floatBg?: string;
  /** Resting label colour (floating mode only). */
  floatColor?: string;
  /** Floated label colour (floating mode only). */
  floatColorActive?: string;
  /** Left inset of the floating label, matching the control's padding. */
  floatInset?: number | string;
  /** Compact floating labels for dense panels. */
  floatSmall?: boolean;
  /** Optional wrapper for the whole block. */
  className?: string;
  style?: CSSProperties;
  /** Slot rendered between the contact fields and the location fields. */
  middleSlot?: ReactNode;
}

const DEFAULT_ERROR_CLASS = "mt-1.5 text-[13px] text-[#B91C1C]";

export default function LeadCoreFields({
  values,
  errors,
  onChange,
  idPrefix,
  disabled,
  labelStyle,
  inputStyle,
  inputClassName,
  errorClassName,
  showLabels = true,
  floatBg,
  floatColor,
  floatColorActive,
  floatInset,
  floatSmall,
  className,
  style,
  middleSlot,
}: LeadCoreFieldsProps) {
  const errClass = errorClassName ?? DEFAULT_ERROR_CLASS;
  const floating = !showLabels;

  const wrap = (key: string, label: string, filled: boolean, control: ReactNode) =>
    floating ? (
      <FloatingField
        id={`${idPrefix}-${key}`}
        label={`${label} *`}
        filled={filled}
        bg={floatBg}
        color={floatColor}
        floatColor={floatColorActive}
        inset={floatInset}
        small={floatSmall}
      >
        {control}
      </FloatingField>
    ) : (
      control
    );

  const text = (
    key: keyof LeadCoreValues,
    label: string,
    placeholder: string,
    type: "text" | "email" | "tel" = "text",
    extra?: Partial<React.InputHTMLAttributes<HTMLInputElement>>,
  ) => {
    const id = `${idPrefix}-${key}`;
    const input = (
      <input
        id={id}
        name={key}
        type={type}
        value={values[key]}
        onChange={(e) => onChange(key, e.target.value)}
        placeholder={floating ? "" : placeholder}
        aria-label={label}
        required
        aria-required="true"
        aria-invalid={!!errors[key]}
        disabled={disabled}
        style={inputStyle}
        className={inputClassName}
        {...extra}
      />
    );
    return (
      <div key={key}>
        {showLabels && (
          <label htmlFor={id} style={labelStyle}>
            {label} *
          </label>
        )}
        {wrap(key, label, values[key].trim().length > 0, input)}
        {errors[key] && (
          <p role="alert" className={errClass}>
            {errors[key]}
          </p>
        )}
      </div>
    );
  };

  const stateSelect = (
    <select
      id={`${idPrefix}-state`}
      name="state"
      value={values.state}
      onChange={(e) => onChange("state", e.target.value)}
      aria-label="State"
      required
      aria-required="true"
      aria-invalid={!!errors.state}
      autoComplete="address-level1"
      disabled={disabled}
      style={inputStyle}
      className={inputClassName}
    >
      {/* In floating mode the label already reads "State", so the empty option
          stays blank to avoid two overlapping pieces of text. */}
      <option value="">{floating ? "" : "Select state"}</option>
      {AU_STATES.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );

  return (
    <div className={className} style={style}>
      {text("firstName", "First name", "First name", "text", {
        autoComplete: "given-name",
      })}
      {text("lastName", "Last name", "Last name", "text", {
        autoComplete: "family-name",
      })}
      {text("business", "Business name", "Business name", "text", {
        autoComplete: "organization",
      })}
      {text("email", "Email", "you@yourbusiness.com.au", "email", {
        autoComplete: "email",
        inputMode: "email",
      })}
      {text("phone", "Phone", "04XX XXX XXX", "tel", {
        autoComplete: "tel",
        inputMode: "tel",
      })}
      {middleSlot}
      {text("suburb", "Suburb", "Suburb", "text", { autoComplete: "address-level2" })}
      <div>
        {showLabels && (
          <label htmlFor={`${idPrefix}-state`} style={labelStyle}>
            State *
          </label>
        )}
        {/* The empty option renders blank, so the label rests inside the
            control like every other field until a state is chosen. */}
        {wrap("state", "State", values.state.trim().length > 0, stateSelect)}
        {errors.state && (
          <p role="alert" className={errClass}>
            {errors.state}
          </p>
        )}
      </div>
      {text("postcode", "Postcode", "Postcode", "text", {
        inputMode: "numeric",
        pattern: "\\d{4}",
        maxLength: 4,
        autoComplete: "postal-code",
      })}
    </div>
  );
}

